/**
 * Route → required-permission map (03-07 §3).
 *
 * A route lists the permission keys that grant access; the subject needs **at least one**. An
 * empty list means "any authenticated subject".
 *
 * ## Ids are admin's real paths
 *
 * The ids below are the paths `apps/admin` actually routes, in TanStack's `$param` spelling, so a
 * consumer can map a route file to its entry without a translation table. The original id set was
 * seeded from a pre-rename config and had drifted from every one of them: `/labs` for
 * `/laboratories`, `/admin/rfid-tags` for `/rfid-tags`, `/users/:userId/scopes` for a route HS-847
 * reshaped into `/users/$id/$tenantId/access`, and `/reports/hood-usage` for a route that does not
 * exist. Nothing consumed the map, so nothing broke — but nothing worked either.
 *
 * `/institutions` is the one id that has been spelled both ways. It was `/institutions` when the
 * map was seeded, moved to `/institutes` to match the route admin actually served, and moved back
 * when HS-1212 renamed that route. Admin still answers `/institutes/*` through redirect routes for
 * bookmarks; those carry no permission entry, because the redirect resolves before the target
 * route's guard runs and it is the target that gates.
 *
 * `apps/portal` is not in the map. Its routes (`/sessions`, `/files`, `/sash-iq`,
 * `/notifications`, `/profile`) are all own-scope screens with no permission gate today; the
 * earlier HS-849 entries described Group sub-routes portal no longer has. `/notifications` does
 * have an entry since HS-1548, but for admin's own screen of that path, ungated for the same reason.
 *
 * ## Keys mirror the backend attribute, and where they cannot, they say so
 *
 * Each entry was read off the `[AuthorizePermission]` attribute on the endpoint the screen calls
 * (`sash-admin-api` @ `development`, 2026-08-14). Two classes of exception are marked `UI-ONLY`
 * inline: endpoints the backend leaves on a bare `[Authorize]` (so the gate here is a product
 * decision, not a mirror), and the one endpoint whose attribute is unsatisfiable.
 *
 * Normative: these checks are UI show/hide ONLY — the server is authoritative (03-07 §1). Gating
 * a screen here never makes an unguarded endpoint safe.
 */

import { hasPermission, type Subject } from './checks';
import type { PermissionKey } from './matrix.config';

export type RouteId =
  | '/dashboard'
  | '/notifications'
  | '/institutions'
  | '/institutions/new'
  | '/institutions/$id'
  | '/institutions/$id/edit'
  | '/institutions/$id/storage'
  | '/groups'
  | '/groups/new'
  | '/groups/$id'
  | '/groups/$id/edit'
  | '/laboratories'
  | '/laboratories/new'
  | '/laboratories/$id'
  | '/laboratories/$id/edit'
  | '/devices'
  | '/devices/new'
  | '/devices/$id'
  | '/devices/$id/edit'
  | '/users'
  | '/users/new/$tenantId'
  | '/users/import/$tenantId'
  | '/users/$id/$tenantId'
  | '/users/$id/$tenantId/edit'
  | '/users/$id/$tenantId/access'
  | '/users/$id/$tenantId/access/new'
  | '/users/$id/$tenantId/access/$grantId/edit'
  | '/users/$id/$tenantId/move'
  | '/users/$id/$tenantId/permissions'
  | '/certifications'
  | '/certifications/$id'
  | '/certifications/$id/edit'
  | '/rfid-tags'
  | '/rfid-tags/new'
  | '/streams'
  | '/streams/$sessionId'
  | '/requests'
  | '/requests/$id'
  | '/installations'
  | '/installations/new'
  | '/installations/$id'
  | '/installations/$id/edit'
  | '/installer'
  | '/installer/installations/$id'
  | '/installer/jobs'
  | '/installer/jobs/$id'
  | '/reports'
  | '/reports/new'
  | '/reports/$id'
  | '/reports/schedules'
  | '/reports/schedules/$id/edit'
  | '/logs'
  | '/settings';

export const ROUTE_PERMISSIONS: Record<RouteId, readonly PermissionKey[]> = {
  // Always reachable. It is the redirect target when a guard refuses somewhere else, and the one
  // screen a subject with no grants at all can still land on.
  '/dashboard': [],

  // HS-1548: the signed-in user's own notifications feed (`api/portal/me/notifications`, token
  // caller only). Own-scope like the portal screen of the same path, so it gates on nothing.
  '/notifications': [],

  // `GET /api/Institution` and `GET /api/Institution/{id}` both ask
  // `Institution.Manage,Institution.ViewAggregates`. `Institution.View`/`ViewAll` are seeded but
  // no endpoint reads them, so gating on those would hide the screen from people the API admits.
  '/institutions': ['Institution.Manage', 'Institution.ViewAggregates'],
  // HS-1201: creating an institution is Hawkins-only. `InstitutionAdmin` holds `Institution.Manage`
  // — it manages the one it belongs to — but deliberately not `Institution.Create`, so gating the
  // create route on `Manage` let it reach a form the API refuses.
  '/institutions/new': ['Institution.Create'],
  '/institutions/$id': ['Institution.Manage', 'Institution.ViewAggregates'],
  '/institutions/$id/edit': ['Institution.Manage'],
  // HS-1508: `GET /api/Institution/{id}/storage` and `…/storage/users` (HS-1507) ask the same pair
  // as the detail page. Edits on the page gate per control through the action map.
  '/institutions/$id/storage': ['Institution.Manage', 'Institution.ViewAggregates'],

  // UI-ONLY (read): `GET /api/Group` and `GET /api/Group/{id}` carry no permission attribute at
  // all. Writes ask `Institution.AdministerHierarchy,Group.Manage`. Gating the read on the same
  // keys keeps the section coherent — a subject who can neither create nor edit a group has no
  // use for the list — but it is a product decision, not a mirror of a server check.
  '/groups': ['Institution.AdministerHierarchy', 'Group.Manage'],
  '/groups/new': ['Institution.AdministerHierarchy', 'Group.Manage'],
  '/groups/$id': ['Institution.AdministerHierarchy', 'Group.Manage'],
  '/groups/$id/edit': ['Institution.AdministerHierarchy', 'Group.Manage'],

  '/laboratories': ['Lab.ViewAll'],
  '/laboratories/new': ['Lab.Create'],
  '/laboratories/$id': ['Lab.View', 'Lab.ViewAll'],
  '/laboratories/$id/edit': ['Lab.Update'],

  '/devices': ['Device.ViewAll'],
  '/devices/new': ['Device.Create'],
  '/devices/$id': ['Device.View', 'Device.ViewAll'],
  '/devices/$id/edit': ['Device.Update'],

  '/users': ['User.ViewAll'],
  '/users/new/$tenantId': ['User.Create'],
  // HS-1611 (D16): the import endpoint (HS-1642) creates users *and* grants them scopes, so it
  // needs `User.Create` and `Scope.Assign`. A route entry grants on any one key, so the route
  // gates on `User.Create` and the page refuses a session without `Scope.Assign` itself.
  '/users/import/$tenantId': ['User.Create'],
  '/users/$id/$tenantId': ['User.ViewOwn', 'User.ViewAll'],
  // UI-ONLY: `PUT /api/User/{institutionId}/{userId}` carries no permission attribute — any
  // authenticated caller may edit any user in their institution. `User.Update` is the seeded key
  // for the intent (nothing reads it), so it is the honest gate to show, and it is deliberately
  // stricter than the API until the backend attributes that endpoint.
  '/users/$id/$tenantId/edit': ['User.Update'],
  // Assignments, not the `[Obsolete]` scope routes: `AssignmentController` reads on
  // `Institution.AdministerHierarchy,Scope.ViewAll` and writes on
  // `Institution.AdministerHierarchy,Scope.Assign`. `Scope.ViewOwn` does not open this screen —
  // it opens a user's view of their *own* scopes, which is a different surface.
  '/users/$id/$tenantId/access': ['Institution.AdministerHierarchy', 'Scope.ViewAll'],
  // HS-1719: the grant form is its own screen now. `POST …/roles` and `PUT …/roles` both ask
  // `Institution.AdministerHierarchy,Scope.Assign`, so the form routes gate on the write keys —
  // a subject who can only read grants (`Scope.ViewAll`) must not reach a form the API refuses.
  '/users/$id/$tenantId/access/new': ['Institution.AdministerHierarchy', 'Scope.Assign'],
  '/users/$id/$tenantId/access/$grantId/edit': ['Institution.AdministerHierarchy', 'Scope.Assign'],
  // HS-1288: `POST /api/User/{institutionId}/{userId}/move` asks
  // `Institution.AdministerHierarchy,User.Update`, and additionally requires `Institution` scope
  // access to *both* the source and the target — a pair check no route gate can mirror, so the
  // screen degrades honestly when the second institution is out of reach rather than pretending
  // the gate covered it.
  '/users/$id/$tenantId/move': ['Institution.AdministerHierarchy', 'User.Update'],
  // `UserPermissionController` reads on `Permission.ViewOwn,Permission.ViewAll` and saves on
  // `Permission.Assign`. The previous entry demanded `FullAccess`, which locked out every
  // InstitutionAdmin and LabAdmin — both hold all three keys.
  '/users/$id/$tenantId/permissions': ['Permission.ViewOwn', 'Permission.ViewAll'],

  // The screen lists certification *programmes* — the per-laboratory training material — not the
  // per-user `Certification.*` records, which are a different resource behind different keys.
  '/certifications': ['CertificationProgram.ViewAll'],
  '/certifications/$id': ['CertificationProgram.ViewAll'],
  '/certifications/$id/edit': ['CertificationProgram.Manage'],

  // UI-ONLY (read): `GET /api/rfid/tags` and `/tags/list` carry no permission attribute.
  // `Rfid.ViewAll` is the seeded key for the intent; creating a card really does ask
  // `Rfid.CreateCard`.
  '/rfid-tags': ['Rfid.ViewAll'],
  '/rfid-tags/new': ['Rfid.CreateCard'],

  // UI-ONLY: `StreamsController`, `StreamSessionsController` and `StreamParticipantsController`
  // are all bare `[Authorize]` — the REST surface checks nothing. `Video.*` is enforced only
  // inside `TeachingOrchestrationService`. Gating here is the product's call (confirmed
  // 2026-08-14) and enforces more than the API does.
  '/streams': ['Video.Observer'],
  '/streams/$sessionId': ['Video.Observer'],

  // HS-1548: `GET /api/service-requests` and `GET /api/service-requests/{id}` both ask
  // `Request.View,Request.Manage`. Which institutions' requests come back is decided by the
  // backend per institution; this only mirrors the coarse attribute.
  '/requests': ['Request.View', 'Request.Manage'],
  '/requests/$id': ['Request.View', 'Request.Manage'],

  // HS-980, target contract (HS-1620, HS-1621): the list and detail ask `Installation.View`, every
  // write asks `Installation.Configure`. UI-ONLY until those land — the screens read the mock
  // transport meanwhile (`apps/admin/src/tmp/`); HS-1543's served `GET /api/Installation` already
  // checks `Installation.View`.
  '/installations': ['Installation.View'],
  '/installations/new': ['Installation.Configure'],
  '/installations/$id': ['Installation.View'],
  '/installations/$id/edit': ['Installation.Configure'],

  // HS-980 installer workspace (HS-1622): every `/api/installer/me/*` route asks
  // `Installation.AcceptanceTest` and is scoped to the caller's own assignments. UI-ONLY until
  // HS-1622 lands — the screens read the mock transport meanwhile. Which SHELL a session gets is a
  // separate, role-based choice (`apps/admin/src/services/auth/workspace.ts`) and never authorizes.
  '/installer': ['Installation.AcceptanceTest'],
  '/installer/installations/$id': ['Installation.AcceptanceTest'],
  '/installer/jobs': ['Installation.AcceptanceTest'],
  '/installer/jobs/$id': ['Installation.AcceptanceTest'],

  // HS-961, enforced by the Admin API since HS-1592: reading templates, runs, their detail and
  // preview asks `Report.View`; generating, downloading and every schedule route ask
  // `Report.Export`. The screens read `/api/reports/*` since HS-1659.
  '/reports': ['Report.View'],
  '/reports/new': ['Report.Export'],
  '/reports/$id': ['Report.View'],
  '/reports/schedules': ['Report.Export'],
  '/reports/schedules/$id/edit': ['Report.Export'],

  '/logs': ['System.ViewLogs'],

  // Per-user preferences, no server resource behind them.
  '/settings': [],
};

/**
 * Whether the subject may open `routeId`. Routes with no required permissions are
 * open to any authenticated subject; otherwise the subject needs at least one of
 * the route's permissions.
 */
export function canAccessRoute(routeId: RouteId, subject: Subject): boolean {
  const required = ROUTE_PERMISSIONS[routeId];
  if (required.length === 0) return true;
  return required.some((key) => hasPermission(subject, key));
}
